import React from 'react'
import moment from 'moment-timezone'
import classnames from 'classnames'
import { Button } from '@abroad/components'
import {
  getSlotRestrictionMessage,
  isSameDayConflictSlot,
  isWeeklyLimitWarningSlot,
  isMaxSessionsPerWeekExceededSlot,
  isSelectableDateItem,
  isIncludedDateItem,
} from '@abroad/rest-client'

const CheckIcon = () => (
  <svg
    width='8'
    height='7'
    viewBox='0 0 11 9'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M1 4.5L4 7.5L10 1.5'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const SelectedDatesList = ({
  selectedDates = [],
  timeFormat = '12h',
  timezoneOffsetMinutes = null,
  timezone = null,
  onDateClick = () => {},
  onReset = () => {},
  onToggleInclude = () => {},
  isCheckingAvailability = false,
  showBackButton = false,
  onBackClick = () => {},
}) => {
  const formatTime = (timestamp) => {
    if (!timestamp) return ''
    let baseTime
    if (timezone) {
      // When timezone string is provided, use moment-timezone to convert from UTC
      // This automatically handles DST for the specific date
      baseTime = moment.utc(timestamp).tz(timezone)
    } else if (timezoneOffsetMinutes === null) {
      baseTime = moment(timestamp)
    } else {
      // When only timezone offset is provided, convert from UTC to the specified timezone offset
      baseTime = moment.utc(timestamp).utcOffset(timezoneOffsetMinutes)
    }

    return timeFormat === '12h'
      ? baseTime.format('h:mm A')
      : baseTime.format('HH:mm')
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return moment(dateStr).format('ddd, MMM D, YYYY')
  }

  // Total is every session in the list, including ones the coach can't take (blocked),
  // so it matches the number of sessions being scheduled
  const totalCount = selectedDates.length
  const includedCount = selectedDates.filter(isIncludedDateItem).length

  return (
    <div className='selected-dates-list mt-1 pt-3'>
      <div className='selected-dates-list__header d-flex justify-content-between align-items-center mb-3'>
        <div className='d-flex align-items-center gap-2'>
          {showBackButton && (
            <>
              <Button
                variant='none'
                className='p-0 border-0 box-shadow-none text-saffron-700 hover cursor-pointer mr-2'
                onClick={onBackClick}>
                <svg
                  width='11'
                  height='19'
                  viewBox='0 0 11 19'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M9.49615 1.25L1.25 9.25003'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                  />
                  <path
                    d='M9.49615 17.25L1.25 9.25'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                  />
                </svg>
              </Button>
              <span
                className='text-saffron-700 hover cursor-pointer s8'
                onClick={onBackClick}>
                Back to all dates
              </span>
            </>
          )}
        </div>
        <span className='s6'>Selected dates</span>
        {!showBackButton && (
          <Button
            variant='none'
            className='p-0 border-0 box-shadow-none text-saffron-700 hover cursor-pointer s8'
            onClick={onReset}>
            RESET
          </Button>
        )}
      </div>

      {totalCount > 0 && (
        <div className='selected-dates-list__summary d-flex justify-content-between align-items-center mb-2'>
          <span className='s8'>
            {includedCount} of {totalCount} selected
          </span>
        </div>
      )}

      <div className='selected-dates-list__items'>
        {selectedDates.map((item, index) => {
          const {
            date,
            slotStart,
            isSlotAvailable = true,
            isEditing = false,
          } = item

          const hasError = isSlotAvailable === false
          const isRestriction =
            isSameDayConflictSlot(item) ||
            isWeeklyLimitWarningSlot(item) ||
            isMaxSessionsPerWeekExceededSlot(item)
          const isSelectable = isSelectableDateItem(item)
          const isIncluded = isIncludedDateItem(item)
          const isBlocked = !isSelectable

          return (
            <div
              key={index}
              className={classnames('selected-dates-list__item mb-2 p-3', {
                'selected-dates-list__item--error': hasError,
                'selected-dates-list__item--restriction':
                  hasError && isRestriction,
                'selected-dates-list__item--excluded':
                  isSelectable && !isIncluded,
                'selected-dates-list__item--editing': isEditing,
                'selected-dates-list__item--checking': isCheckingAvailability,
                'cursor-pointer': hasError && !isCheckingAvailability,
              })}
              onClick={
                hasError && !isCheckingAvailability
                  ? () => onDateClick(item, index)
                  : undefined
              }>
              <div className='d-flex align-items-start'>
                <div className='selected-dates-list__lead'>
                  <button
                    type='button'
                    role='checkbox'
                    aria-checked={isIncluded}
                    aria-label={
                      isBlocked
                        ? 'Session cannot be booked until the slot is changed'
                        : isIncluded
                        ? 'Exclude this session'
                        : 'Include this session'
                    }
                    disabled={isBlocked || isCheckingAvailability}
                    onClick={(event) => {
                      event.stopPropagation()
                      onToggleInclude(index)
                    }}
                    className={classnames('selected-dates-list__checkbox', {
                      'is-checked': isIncluded,
                    })}>
                    {isIncluded && <CheckIcon />}
                  </button>
                  <div className='selected-dates-list__icon'>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'>
                      <rect
                        x='2'
                        y='2'
                        width='12'
                        height='12'
                        rx='1'
                        stroke='currentColor'
                        strokeWidth='1.5'
                      />
                      <path
                        d='M5 1V4M11 1V4M2 6H14'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start'>
                    <div className='s8 mt-1 selected-dates-list__date'>
                      {formatDate(date)},{' '}
                      {slotStart ? formatTime(slotStart) : ''}
                    </div>
                  </div>
                  {hasError && (
                    <div
                      className={classnames(
                        's8 mt-1',
                        isRestriction ? 'text-saffron-700' : 'text-danger',
                      )}>
                      {getSlotRestrictionMessage(item) ||
                        'Coach is not available at this slot, please click to update.'}
                      {isBlocked && isSameDayConflictSlot(item) && (
                        <span> Click to choose another date or time.</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SelectedDatesList
